import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Pencil, UserPlus, Phone, Shield } from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import { HELP_CONTENT } from "@/lib/help-content";

type Profile = Tables<"profiles">;
type UserRole = { user_id: string; role: string };

export const TeamManagement = () => {
  const { user } = useAuth();
  const [employees, setEmployees] = useState<Profile[]>([]);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Profile | null>(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newMemberName, setNewMemberName] = useState("");
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [newMemberPhone, setNewMemberPhone] = useState("");
  const [newMemberRole, setNewMemberRole] = useState<"admin" | "agent">("agent");
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .order('full_name');

    if (error) {
      console.error('Error fetching employees:', error);
      toast.error("Failed to load employees");
    } else {
      setEmployees(data || []);
    }
    
    const { data: roles } = await supabase
      .from('user_roles')
      .select('user_id, role');
    
    setUserRoles(roles || []);
    setLoading(false);
  };

  const handleRoleChange = async (userId: string, newRole: string | null) => {
    if (newRole === null) {
      // Remove role
      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId)
        .eq('role', 'admin');
      
      if (error) {
        console.error('Error removing role:', error);
        toast.error("Failed to remove admin role");
      } else {
        toast.success("Admin role removed");
        fetchEmployees();
      }
    } else {
      // Add role
      const { error } = await supabase
        .from('user_roles')
        .insert([{ user_id: userId, role: newRole as 'admin' | 'agent' }]);
      
      if (error) {
        console.error('Error adding role:', error);
        toast.error("Failed to add admin role");
      } else {
        toast.success("Admin role added");
        fetchEmployees();
      }
    }
  };

  const getUserRole = (userId: string): string | null => {
    const role = userRoles.find(r => r.user_id === userId);
    return role?.role || null;
  };

  const handleEditPhone = (employee: Profile) => {
    setEditingEmployee(employee);
    setPhoneNumber(employee.phone_number || "");
    setIsDialogOpen(true);
  };

  const handleSavePhone = async () => {
    if (!editingEmployee) return;

    // Validate phone number format (E.164)
    const phoneRegex = /^\+[1-9]\d{1,14}$/;
    if (phoneNumber && !phoneRegex.test(phoneNumber)) {
      toast.error("Phone number must be in E.164 format (e.g., +1234567890)");
      return;
    }

    const { error } = await supabase
      .from('profiles')
      .update({ phone_number: phoneNumber })
      .eq('id', editingEmployee.id);

    if (error) {
      console.error('Error updating phone:', error);
      toast.error("Failed to update phone number: " + error.message);
    } else {
      toast.success("Phone number updated successfully");
      setIsDialogOpen(false);
      fetchEmployees();
    }
  };

  const handleAddTeamMember = async () => {
    // Validate inputs
    if (!newMemberName.trim()) {
      toast.error("Please enter a full name");
      return;
    }

    if (!newMemberEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newMemberEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    // Validate phone number if provided
    if (newMemberPhone) {
      const phoneRegex = /^\+[1-9]\d{1,14}$/;
      if (!phoneRegex.test(newMemberPhone)) {
        toast.error("Phone number must be in E.164 format (e.g., +1234567890)");
        return;
      }
    }

    setIsCreating(true);
    
    try {
      // Call edge function to create team member
      const { data, error } = await supabase.functions.invoke('create-team-member', {
        body: {
          email: newMemberEmail.trim(),
          fullName: newMemberName.trim(),
          phoneNumber: newMemberPhone.trim() || null,
          role: newMemberRole
        }
      });

      if (error) throw error;

      toast.success(`Team member ${newMemberName} created successfully! Password reset email sent.`);
      
      // Reset form and close dialog
      setNewMemberName("");
      setNewMemberEmail("");
      setNewMemberPhone("");
      setNewMemberRole("agent");
      setIsAddDialogOpen(false);
      
      // Refresh employee list
      fetchEmployees();
    } catch (error: any) {
      console.error('Error creating team member:', error);
      toast.error(error.message || "Failed to create team member");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Team Management
              <HelpTooltip {...HELP_CONTENT.dashboard.teamTab} />
            </CardTitle>
            <CardDescription>
              Manage employee phone numbers and assign roles (Admin or Agent)
            </CardDescription>
          </div>
          <Button onClick={() => setIsAddDialogOpen(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Add Team Member
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p>Loading employees...</p>
        ) : (
          <div className="w-full overflow-visible">
            <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone Number</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {employees.map((employee) => {
                const role = getUserRole(employee.id);
                const isCurrentUser = user?.id === employee.id;
                return (
                  <TableRow key={employee.id}>
                    <TableCell>{employee.full_name || "N/A"}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{employee.email}</TableCell>
                    <TableCell>
                      {employee.phone_number ? (
                        <span className="flex items-center gap-2">
                          <Phone className="h-4 w-4" />
                          {employee.phone_number}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Not set</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {isCurrentUser ? (
                        <div className="flex items-center gap-2">
                          <Badge variant={role === "admin" ? "default" : "secondary"}>
                            {role === "admin" ? (
                              <span className="flex items-center gap-2">
                                <Shield className="h-4 w-4" />
                                Admin (You)
                              </span>
                            ) : (
                              "Agent (You)"
                            )}
                          </Badge>
                          <HelpTooltip {...HELP_CONTENT.team.ownRole} />
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Select
                            value={role || "agent"}
                            onValueChange={(value) => handleRoleChange(employee.id, value === "agent" ? null : value)}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="agent">Agent</SelectItem>
                              <SelectItem value="admin">
                                <div className="flex items-center gap-2">
                                  <Shield className="h-4 w-4" />
                                  Admin
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <HelpTooltip {...HELP_CONTENT.team.roles} />
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditPhone(employee)}
                      >
                        <Pencil className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div>
        )}

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Phone Number</DialogTitle>
              <DialogDescription>
                Update phone number for {editingEmployee?.full_name || editingEmployee?.email}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="phone" className="flex items-center gap-2">
                  Phone Number
                  <HelpTooltip {...HELP_CONTENT.team.phoneNumber} />
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+1234567890"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Format: +[country code][number] (e.g., +15551234567)
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSavePhone}>
                  Save
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Add New Team Member</DialogTitle>
              <DialogDescription>
                Create a new team member account. They will receive a password reset email to set their password.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="fullName">Full Name *</Label>
                <Input
                  id="fullName"
                  type="text"
                  placeholder="John Doe"
                  value={newMemberName}
                  onChange={(e) => setNewMemberName(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="john@example.com"
                  value={newMemberEmail}
                  onChange={(e) => setNewMemberEmail(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="phone" className="flex items-center gap-2">
                  Phone Number (Optional)
                  <HelpTooltip {...HELP_CONTENT.team.phoneNumber} />
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+1234567890"
                  value={newMemberPhone}
                  onChange={(e) => setNewMemberPhone(e.target.value)}
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Format: +[country code][number] (e.g., +15551234567)
                </p>
              </div>
              <div>
                <Label htmlFor="role">Role *</Label>
                <Select value={newMemberRole} onValueChange={(value: "admin" | "agent") => setNewMemberRole(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agent">Agent</SelectItem>
                    <SelectItem value="admin">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        Admin
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setIsAddDialogOpen(false);
                    setNewMemberName("");
                    setNewMemberEmail("");
                    setNewMemberPhone("");
                    setNewMemberRole("agent");
                  }}
                  disabled={isCreating}
                >
                  Cancel
                </Button>
                <Button onClick={handleAddTeamMember} disabled={isCreating}>
                  {isCreating ? "Creating..." : "Create Account"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};
